import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Are you a lender?',
      answer: 'No, we are not a lender. We are a funding strategy and business credit consulting firm. We don\'t sell loan products or provide funding directly. Instead, we prepare, position, and package you to qualify for funding from banks, credit card issuers, and other legitimate financial institutions. Our role is to make you fundable, not to fund you ourselves.'
    },
    {
      question: 'Do you guarantee approvals?',
      answer: 'No one can guarantee approvals — anyone who promises that is not being honest. What we do guarantee is that we\'ll prepare you properly, position you strategically, and execute with precision. We\'ll tell you the truth about what\'s possible, what needs work, and what timeline is realistic. Our goal is to maximize your approval rates and funding amounts through proper preparation and strategic positioning.'
    },
    {
      question: 'Will this hurt my credit?',
      answer: 'Our preparation and positioning work does not involve credit inquiries. When we eventually submit applications on your behalf (or guide you to submit them), those applications will result in hard inquiries, which is normal for any funding application. However, we strategically time and sequence applications to minimize impact and maximize results. We also work to improve your overall credit profile before applying, which often offsets any temporary inquiry impact.'
    },
    {
      question: 'How fast can I get funded?',
      answer: 'Timeline varies based on your current situation. If you already have strong fundability (good credit, proper business structure, clean documentation), you might see approvals within 30-60 days. If you need significant preparation work (entity setup, credit building, compliance corrections), it could take 3-6 months before you\'re ready to apply. We\'ll give you an honest assessment during pre-qualification and create a realistic timeline for your specific situation.'
    },
    {
      question: 'What if my business is new?',
      answer: 'New businesses can still access funding, but the approach is different. We focus on personal credit positioning, proper business structure setup, and strategic use of personal guarantees to build initial business credit. As your business ages and establishes credit history, we transition to more business-focused funding strategies. The key is starting with the right foundation and building systematically.'
    },
    {
      question: 'What\'s the difference between business funding and personal funding?',
      answer: 'Business funding uses your business credit profile and structure to qualify for business credit cards, lines of credit, and loans. Personal funding uses your personal credit profile to qualify for personal loans and credit cards. Many business owners need both — personal funding for immediate needs and business funding for long-term growth. We help you understand which path (or combination) makes sense for your situation.'
    },
    {
      question: 'Do I need perfect credit to work with you?',
      answer: 'No, but you do need decent credit and a willingness to improve it if necessary. We work with clients across the credit spectrum, but we\'re honest about what\'s realistic at each level. If your credit needs significant work, we\'ll tell you that upfront and create a plan to improve it before applying for funding. Our goal is to position you for success, not to waste your time with applications you\'re not ready for.'
    },
    {
      question: 'What does "fundability" mean?',
      answer: 'Fundability is how lenders see you when evaluating your application. It includes your credit scores, but also your business structure, compliance, documentation, banking relationships, and how you present yourself on paper. Many people with good credit scores still get denied because they lack fundability in other areas. We fix all of these issues before you apply, so lenders see you as a strong, low-risk candidate.'
    },
    {
      question: 'How is this different from credit repair?',
      answer: 'Credit repair focuses on removing negative items from credit reports. We focus on building fundable profiles through proper structure, strategic credit building, and professional positioning. While we may address credit issues as part of our process, our approach is much broader and more strategic. We\'re preparing you to qualify for funding, not just improving your credit score.'
    },
    {
      question: 'What kind of results can I expect?',
      answer: 'Results vary based on your starting point, but our clients typically see significant improvements in approval rates and funding amounts compared to applying on their own. Many clients who were previously denied are able to qualify after working with us. The key is proper preparation — when you\'re truly fundable, approvals follow naturally. We\'ll give you realistic expectations during your pre-qualification assessment.'
    },
    {
      question: 'Do you work with startups?',
      answer: 'Yes, but our approach for startups is different than for established businesses. We focus on personal credit positioning, proper entity setup, and building initial business credit through strategic tradelines and vendor accounts. As your startup grows and establishes history, we transition to more traditional business funding strategies. The key is starting with the right foundation.'
    },
    {
      question: 'What information do you need to get started?',
      answer: 'We\'ll need basic information about you, your business (if applicable), your credit situation, and your funding goals. During pre-qualification, we\'ll ask about your business structure, revenue, credit scores, and what you need funding for. The more transparent you are with us, the better we can assess your situation and create an effective strategy.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-4 font-semibold">
              FREQUENTLY ASKED QUESTIONS
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              Your Questions Answered
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about our funding strategy and business credit consulting services
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-navy pr-8">
                      {faq.question}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-xl text-navy`}></i>
                    </div>
                  </button>
                  
                  {openIndex === index && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-navy mb-6">
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 mb-10">
              Start your pre-qualification and we'll answer all your questions during the assessment
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center bg-navy text-white px-8 py-4 rounded-lg text-base font-medium hover:bg-navy-light transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
